import React from 'react';
import { motion } from 'framer-motion';
import { Shield, BookOpen, Code, Users, Eye, Globe, Scale, FileText, AlertTriangle, Mail } from 'lucide-react';

const Conditions = () => {
  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Sections with icons
  const sections = [
    {
      title: "Acceptation des conditions",
      icon: <Shield className="text-blue-400" size={24} />,
      content: "En accédant et en utilisant le site web du CFA Est Loiret, vous acceptez de vous conformer et d'être lié par les présentes conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser ce site."
    },
    {
      title: "Utilisation du site",
      icon: <BookOpen className="text-blue-400" size={24} />,
      content: "Le contenu de ce site est fourni à titre informatif uniquement. L'utilisation du site est à vos propres risques. Le CFA Est Loiret se réserve le droit de modifier le contenu du site à tout moment sans préavis."
    },
    {
      title: "Propriété intellectuelle",
      icon: <Code className="text-blue-400" size={24} />,
      content: "L'ensemble du contenu présent sur ce site - incluant textes, images, logos, vidéos et tout autre élément - est protégé par les lois sur la propriété intellectuelle et appartient au CFA Est Loiret ou à ses partenaires. Toute reproduction, distribution, modification ou utilisation du contenu sans autorisation écrite préalable est strictement interdite."
    },
    {
      title: "Comptes utilisateurs",
      icon: <Users className="text-blue-400" size={24} />,
      content: "Certaines fonctionnalités de ce site peuvent nécessiter la création d'un compte utilisateur. Vous êtes responsable de maintenir la confidentialité de vos informations de connexion et de toutes les activités qui se produisent sous votre compte."
    },
    {
      title: "Comportement des utilisateurs",
      icon: <Eye className="text-blue-400" size={24} />,
      content: (
        <>
          <p>En utilisant ce site, vous acceptez de ne pas :</p>
          <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-300">
            <li>Violer les lois applicables ou les droits des tiers</li>
            <li>Publier ou transmettre du contenu offensant, abusif ou illégal</li>
            <li>Tenter d'accéder sans autorisation à des parties sécurisées du site</li>
            <li>Utiliser le site d'une manière qui pourrait endommager, désactiver ou surcharger l'infrastructure</li>
            <li>Collecter des informations sur d'autres utilisateurs sans leur consentement</li>
          </ul>
        </>
      )
    },
    {
      title: "Liens externes",
      icon: <Globe className="text-blue-400" size={24} />,
      content: "Ce site peut contenir des liens vers des sites web tiers. Ces liens sont fournis uniquement pour votre commodité. Le CFA Est Loiret n'a aucun contrôle sur le contenu de ces sites et n'assume aucune responsabilité pour leur contenu ou leur politique de confidentialité."
    },
    {
      title: "Limitation de responsabilité",
      icon: <AlertTriangle className="text-blue-400" size={24} />,
      content: "Le CFA Est Loiret s'efforce de maintenir les informations de ce site à jour et exactes. Cependant, nous ne garantissons pas l'exactitude, l'exhaustivité ou la pertinence des informations fournies. En aucun cas, le CFA Est Loiret ne pourra être tenu responsable des dommages directs ou indirects résultant de l'utilisation de ce site."
    },
    {
      title: "Modification des conditions",
      icon: <FileText className="text-blue-400" size={24} />,
      content: "Le CFA Est Loiret se réserve le droit de modifier ces conditions d'utilisation à tout moment. Les modifications entrent en vigueur dès leur publication sur le site. Il est de votre responsabilité de consulter régulièrement ces conditions."
    },
    {
      title: "Loi applicable",
      icon: <Scale className="text-blue-400" size={24} />,
      content: "Les présentes conditions d'utilisation sont régies par la loi française. Tout litige relatif à l'interprétation ou à l'exécution de ces conditions sera soumis à la compétence exclusive des tribunaux français."
    },
    {
      title: "Contact",
      icon: <Mail className="text-blue-400" size={24} />,
      content: "Pour toute question concernant ces conditions d'utilisation, vous pouvez nous contacter à l'adresse suivante : contact@cfa-montargis.com"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white pt-24">
      <div className="container mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold mb-4 text-white">
            Conditions d'utilisation
          </h1>
          <div className="h-1 w-24 bg-blue-500 mx-auto rounded-full"></div>
        </motion.div>
        
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-4xl mx-auto"
        >
          {sections.map((section, index) => (
            <motion.div 
              key={index}
              variants={item}
              className="mb-12 bg-slate-800/50 p-6 rounded-lg border border-slate-700 backdrop-blur-sm hover:shadow-lg hover:shadow-blue-900/10 transition-all duration-300"
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-slate-700/50 rounded-lg mr-4">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-semibold text-white">
                  {index + 1}. {section.title}
                </h2>
              </div>
              <div className="pl-14 text-gray-300 leading-relaxed">
                {section.content}
              </div>
            </motion.div>
          ))}
          
          <motion.div 
            variants={item}
            className="mt-16 text-center text-gray-400 italic"
          >
            <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Conditions;